import { useCallback, useEffect, useMemo, useState } from 'react';
import { TreeEntry } from '../types';
import { filterTreeItems, getFullyExpandedIds } from '../utils';

export interface UseTreeSearch {
  expandedIds: Set<string>;
  onExpand: (item: TreeEntry) => void;
  items: TreeEntry[];
}

export interface UseTreeSearchOptions {
  searchKeyword?: string;
  expandedIds?: string[];
  defaultExpandedIds?: string[];
  items: TreeEntry[];
}

export function useTreeSearch({
  searchKeyword = '',
  expandedIds,
  defaultExpandedIds,
  items,
}: UseTreeSearchOptions): UseTreeSearch {
  const [expandedIdsState, setExpandedIdsState] = useState<Set<string>>(new Set());
  const [expandedIdsSearchState, setExpandedIdsSearchState] = useState<Set<string>>(new Set());
  const expandedIdsSet = useMemo(() => (expandedIds != null ? new Set(expandedIds) : null), [expandedIds]);
  const isSearching = searchKeyword.trim() !== '';
  const filteredItems = useMemo(() => filterTreeItems(items, searchKeyword), [items, searchKeyword]);

  const handleExpand = useCallback(
    (item: TreeEntry) => {
      if (item.hasChildren || (item.children != null && item.children.length > 0)) {
        const update = (prev: Set<string>) => {
          const result = new Set(prev);
          if (result.has(item.id)) {
            result.delete(item.id);
          } else {
            result.add(item.id);
          }
          return result;
        };
        if (isSearching) {
          setExpandedIdsSearchState(update);
        } else {
          setExpandedIdsState(update);
        }
      }
    },
    [isSearching],
  );

  useEffect(() => {
    if (isSearching) {
      setExpandedIdsSearchState(new Set(filteredItems.expandedIds));
    } else if (defaultExpandedIds) {
      setExpandedIdsState(new Set(getFullyExpandedIds(items, defaultExpandedIds)));
    }
  }, [isSearching, filteredItems.expandedIds, items, defaultExpandedIds]);

  return {
    expandedIds: isSearching ? expandedIdsSearchState : expandedIdsSet ?? expandedIdsState,
    onExpand: handleExpand,
    items: filteredItems.items,
  };
}
